"use client"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useSendTestQualityDigestMutation,
  useUpdateQualityDigestSettingsMutation,
} from "@/hooks/mutations/conversation-quality"
import { useNotificationChannelOptions } from "@/hooks/notifications/use-channel-options"
import type { NotificationChannelView } from "@/hooks/notifications/use-notification-channels"
import { useQualityDigestSettingsQuery } from "@/hooks/queries/conversation-quality"
import { weeklyRunDay } from "@/lib/conversation-quality/suggestions/checkpoints"
import type { QualityDigestSettingsView } from "@/server-actions/conversation-quality-digest"
import { toDestinationCandidates } from "./destinations"
import { EventSubscriptionCard } from "./event-subscription-card"
import { useTranslations } from "next-intl"



const CONVERSATION_QUALITY_HREF = "?page=conversation-quality"

const DESCRIPTION =
  "A weekly report on your messaging agents: how they did, what waits for you, what self-learning changed and the " +
  `questions they didn't resolve. It goes out on ${weeklyRunDay()}s, for every week your agents had conversations.`
const UNCONFIGURED_NOTE = "Until you save a choice here, it goes to your Account alerts channels."

export function QualityDigestCard({ channels }: { channels: NotificationChannelView[] }) {

  const tUi = useTranslations("NotificationChannels.NotificationChannelsQualityDigestCard")

  const { data, isError } = useQualityDigestSettingsQuery()
  const update = useUpdateQualityDigestSettingsMutation()
  const sendTest = useSendTestQualityDigestMutation()
  const { options, loading } = useNotificationChannelOptions()

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription><span key={tUi("theConversationQualityDigestSettingsCouldNotBe")}>{tUi("theConversationQualityDigestSettingsCouldNotBe")}</span></AlertDescription>
      </Alert>
    )
  }
  if (!data) return <Skeleton className="h-40 w-full" />

  const off = data.qualityDisabledBy !== null

  return (
    <EventSubscriptionCard
      title={tUi("conversationQualityDigest")}
      description={data.configured ? DESCRIPTION : tUi("value0Value1", { value0: String(DESCRIPTION), value1: String(UNCONFIGURED_NOTE) })}
      subscription={{ enabled: data.enabled, channelIds: data.channelIds }}
      options={options}
      candidates={toDestinationCandidates(channels, options)}
      emptyLabel="Versafy (default)"
      noSystemChannelNote={
        data.fallbackEmail
          ? `Falling back to ${data.fallbackEmail}, your account owner.`
          : "Nowhere — select a working channel, or give your account owner an email address."
      }
      onSave={(values) => update.mutateAsync(values).catch(() => {})}
      saving={update.isPending}
      optionsLoading={loading}
      lockedNotice={off ? <QualityOffNotice disabledBy={data.qualityDisabledBy} /> : undefined}
      actions={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button
            variant="outline"
            size="sm"
            disabled={off || !data.enabled || sendTest.isPending}
            onClick={() => sendTest.mutate()}
          >
            <span key={sendTest.isPending ? tUi("sending") : tUi("sendTest")}>{sendTest.isPending ? tUi("sending") : tUi("sendTest")}</span>
          </Button>
          <p className="min-w-0 flex-1 basis-56 text-xs text-muted-foreground">
            <span key={tUi("sendsTheLatestWeekWithConversationsNowMarked")}>{tUi("sendsTheLatestWeekWithConversationsNowMarked")}</span></p>
        </div>
      }
    />
  )
}

/** Text in spans beside the link, so a translated notice can switch between the two. */
function QualityOffNotice({ disabledBy }: { disabledBy: QualityDigestSettingsView["qualityDisabledBy"] }) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsQualityDigestCard")

  return disabledBy === "platform" ? (
    <span><span key={tUi("conversationQualityIsTurnedOffForThisWorkspace")}>{tUi("conversationQualityIsTurnedOffForThisWorkspace")}</span></span>
  ) : (
    <span>
      <span><span key={tUi("conversationQualityIsTurnedOffForThisWorkspace2")}>{tUi("conversationQualityIsTurnedOffForThisWorkspace2")}</span></span>
      <a className="underline underline-offset-2" href={CONVERSATION_QUALITY_HREF}>
        <span key={tUi("conversationQuality")}>{tUi("conversationQuality")}</span></a>
      <span> <span key={tUi("page")}>{tUi("page")}</span></span>
    </span>
  )
}
